import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { showApiError, showSuccess } from "@/lib/toast";
import { changePassword } from "@/sideEffects/api";
import { useUser } from "@/hooks/useUser";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { useTranslation } from "react-i18next";
import { ROUTES } from "@/constants/routeConstants";

const formSchema = z.object({
  previousPassword: z.string(),
  password: z.string(),
  passwordRepeat: z.string(),
});

type FormSchemaType = z.infer<typeof formSchema>;

export const ChangePasswordPage = () => {
  const { t } = useTranslation();
  const { jwtUserId } = useUser();

  useBreadcrumbs([{ name: t("changePassword.title"), path: ROUTES.CHANGE_PASSWORD }]);

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      previousPassword: "",
      password: "",
      passwordRepeat: "",
    },
  });

  const mutation = useMutation({
    mutationFn: (values: FormSchemaType) => {
      return changePassword(values.previousPassword, values.password, jwtUserId!);
    },
    onSuccess: () => {
      showSuccess(t("changePassword.success"));
    },
    onError: showApiError,
  });

  function onSubmit(values: FormSchemaType) {
    mutation.mutate(values);
  }

  return (
    <Card className="max-w-[550px]">
      <CardHeader>
        <CardTitle className="text-2xl">{t("changePassword.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="previousPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("changePassword.currentPassword")}</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("changePassword.newPassword")}</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="passwordRepeat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("changePassword.confirmPassword")}</FormLabel>
                  <FormControl>
                    <Input type="password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={mutation.isPending}>
              {t("changePassword.button")}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
